import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Plus, FileText, Clock, CheckCircle, XCircle, Send, ArrowRight, TrendingUp, DollarSign, BarChart3, Shuffle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AdminMergeDialog from '../components/admin/AdminMergeDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-50 text-blue-600 border-blue-200', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-600 border-red-200', icon: XCircle },
};

const PIE_COLORS = ['#64748b', '#3b82f6', '#10b981', '#ef4444'];

const formatCurrency = (val) => `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMerge, setShowMerge] = useState(false);
  const isAdmin = (user?.email || '').toLowerCase() === 'info@kenscho.ca';

  useEffect(() => {
    Promise.all([fetchEstimates(), fetchAnalytics()]).finally(() => setLoading(false));
  }, []);

  const fetchEstimates = async () => {
    try {
      const res = await fetch(`${API}/estimates`, { credentials: 'include' });
      if (res.ok) setEstimates(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API}/analytics`, { credentials: 'include' });
      if (res.ok) setAnalytics(await res.json());
    } catch (err) { console.error(err); }
  };

  const recentEstimates = estimates.slice(0, 5);

  const calcTotal = (est) => {
    let sub = 0;
    (est.sections || []).forEach(s => {
      (s.items || []).forEach(item => { sub += (item.qty || 0) * (item.unit_price || 0); });
    });
    return sub + sub * (est.tax_gst || 5) / 100 + sub * (est.tax_qst || 9.975) / 100;
  };

  const statusPieData = analytics ? [
    { name: 'Draft', value: analytics.status_counts.draft },
    { name: 'Sent', value: analytics.status_counts.sent },
    { name: 'Accepted', value: analytics.status_counts.accepted },
    { name: 'Rejected', value: analytics.status_counts.rejected },
  ].filter(d => d.value > 0) : [];

  const monthlyData = (analytics?.monthly || []).map(m => ({
    ...m,
    label: new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
  }));

  return (
    <div className="animate-fade-in" data-testid="dashboard">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">Here's an overview of your estimates.</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowMerge(true)}
            className="flex items-center gap-1.5 text-xs text-[#6B7A73] hover:text-[#2D6B68] border border-[#E6EBE8] rounded-md px-3 py-1.5 hover:border-[#2D6B68] transition-colors"
            title="Merge another account's data into this one"
            data-testid="open-merge-dialog"
          >
            <Shuffle className="w-3.5 h-3.5" />
            Merge account
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Estimates', value: analytics?.total_estimates || estimates.length, icon: FileText, color: '#2D6B68' },
          { label: 'Total Value', value: formatCurrency(analytics?.total_value || 0), icon: DollarSign, color: '#3b82f6', isString: true },
          { label: 'Accepted Value', value: formatCurrency(analytics?.accepted_value || 0), icon: TrendingUp, color: '#10b981', isString: true },
          { label: 'Acceptance Rate', value: `${analytics?.acceptance_rate || 0}%`, icon: BarChart3, color: '#2D6B68', isString: true },
        ].map((stat, i) => (
          <div key={i} className="bg-white border border-[#E6EBE8] rounded-md p-6 hover:-translate-y-0.5 transition-all duration-200" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7A73]">{stat.label}</p>
                <p className="text-2xl font-bold text-[#1A1D1C] mt-2" style={{ fontFamily: stat.isString ? "'IBM Plex Mono', monospace" : "'Chivo', sans-serif" }}>
                  {stat.isString ? stat.value : stat.value}
                </p>
              </div>
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}>
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      {analytics && (analytics.total_estimates > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Monthly Estimates Chart */}
          <div className="lg:col-span-2 bg-white border border-[#E6EBE8] rounded-md p-6" data-testid="monthly-chart">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Monthly Estimate Value</h2>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthlyData} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6EBE8" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6B7A73' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7A73' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E6EBE8', borderRadius: 6, fontSize: 13 }}
                    formatter={(value) => [`$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Total']}
                    labelFormatter={(label) => label}
                  />
                  <Bar dataKey="total" fill="#2D6B68" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="accepted" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-[#6B7A73] text-sm">No monthly data yet</div>
            )}
            <div className="flex items-center gap-6 mt-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#2D6B68]" />
                <span className="text-xs text-[#6B7A73]">Total Value</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#10b981]" />
                <span className="text-xs text-[#6B7A73]">Accepted Value</span>
              </div>
            </div>
          </div>

          {/* Status Pie Chart */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6" data-testid="status-chart">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Status Breakdown</h2>
            {statusPieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={3}>
                      {statusPieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[['Draft','Sent','Accepted','Rejected'].indexOf(statusPieData[i]?.name)] || PIE_COLORS[0]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E6EBE8', borderRadius: 6, fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 mt-2 justify-center">
                  {statusPieData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[['Draft','Sent','Accepted','Rejected'].indexOf(d.name)] || PIE_COLORS[0] }} />
                      <span className="text-xs text-[#6B7A73]">{d.name} ({d.value})</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-[#6B7A73] text-sm">No data yet</div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-3 mb-8">
        <Button data-testid="new-estimate-btn" onClick={() => navigate('/templates')} className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-6 py-2.5 font-medium">
          <Plus className="w-4 h-4 mr-2" /> New Estimate
        </Button>
        <Button data-testid="view-all-btn" onClick={() => navigate('/estimates')} variant="outline" className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-6 py-2.5 font-medium">
          View All Estimates
        </Button>
      </div>

      {/* Recent Estimates */}
      <div className="bg-white border border-[#E6EBE8] rounded-md">
        <div className="px-6 py-4 border-b border-[#E6EBE8] flex items-center justify-between">
          <h2 className="text-lg font-medium text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>Recent Estimates</h2>
          {estimates.length > 5 && (
            <button onClick={() => navigate('/estimates')} className="text-sm text-[#2D6B68] hover:text-[#235552] flex items-center gap-1 font-medium" data-testid="see-all-link">
              See all <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : recentEstimates.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#E6EBE8] mx-auto mb-4" />
            <p className="text-[#6B7A73] text-sm">No estimates yet. Create your first one!</p>
            <Button onClick={() => navigate('/templates')} className="mt-4 bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md" data-testid="create-first-estimate-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Estimate
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-[#E6EBE8]">
            {recentEstimates.map((est) => {
              const cfg = STATUS_CONFIG[est.status] || STATUS_CONFIG.draft;
              const StatusIcon = cfg.icon;
              return (
                <div key={est.estimate_id} className="px-6 py-4 flex items-center justify-between hover:bg-[#F9F9F8] transition-colors cursor-pointer" onClick={() => navigate(`/estimates/${est.estimate_id}`)} data-testid={`estimate-row-${est.estimate_id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-9 h-9 rounded-md bg-[#F5F6F5] flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-[#2D6B68]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1A1D1C] truncate">{est.title}</p>
                      <p className="text-xs text-[#6B7A73]">{est.estimate_number} &middot; {est.client?.name || 'No client'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-[#1A1D1C] hidden sm:block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      ${calcTotal(est).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <Badge className={`${cfg.color} border rounded-full px-2.5 py-0.5 text-xs font-medium flex items-center gap-1`}>
                      <StatusIcon className="w-3 h-3" /> {cfg.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AdminMergeDialog open={showMerge} onOpenChange={setShowMerge} />
    </div>
  );
}
